import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ReactComponent as CloseIcon } from '../../shared/icons/close-icon.svg';

import styles from './styles.module.scss';
import classNames from 'classnames';
interface PropdocsModalProps {
  title: React.ReactElement;
  show: boolean;
  setShow: () => void;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  disableClose?: boolean;
  allowOverflow?: boolean;
  children: React.ReactElement;
}

/**
 * Propdocs Modal
 * - This modal is the base of the modal we use for other modals in the app
 * - Uses React Portal for rendering outside DOM tree for better z-index management
 * - Replaces react-overlays to avoid findDOMNode deprecation warnings
 *
 * @param show - Controls modal visibility
 * @param setShow - Callback to close modal
 * @param size - Modal size: "sm" | "md" | "lg" | "xl"
 * @param title - Modal title as React element
 * @param children - Modal content
 * @param className - Additional CSS classes
 * @param disableClose - Prevents closing the modal
 * @param allowOverflow - Allows overflow for dropdowns and date pickers
 * @constructor
 */
function PropdocsModal({ title, show, setShow, size, children, className, disableClose = false, allowOverflow = false }: PropdocsModalProps) {
  // Access the kebab-case CSS module class directly using bracket notation
  const modalClassNames = classNames(
    size ? styles[`${size}-modal`] : '', 
    allowOverflow && styles.allowOverflow,
    className
  );

  // Handle escape key press
  useEffect(() => {
    if (!show) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShow();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [show, setShow]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [show]);

  // Add cleanup effect to handle ResizeObserver issues when modal closes
  useEffect(() => {
    if (!show) {
      // Small delay to allow any pending ResizeObserver callbacks to complete
      const timeoutId = setTimeout(() => {
        // Force cleanup of any pending ResizeObserver loops
        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
          window.requestAnimationFrame(() => {
            // This helps ensure any ResizeObserver callbacks are processed
          });
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [show]);

  if (!show) return null;

  // Create the modal element with proper z-index
  const modalElement = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1200,
        pointerEvents: 'none',
      }}
    >
      <div style={{ position: 'relative', width: '100%', height: '100%', pointerEvents: 'auto' }}>
        {/* Backdrop */}
        <div
          className={styles.propdocsModal_backdrop}
          onClick={(e) => {
            // Only close if clicking directly on backdrop, not on modal content
            if (e.target === e.currentTarget) {
              setShow();
            }
          }}
        />

        {/* Modal */}
        <div
          className={modalClassNames}
          role="dialog"
          aria-labelledby="modal-label"
          aria-modal="true"
          tabIndex={-1}
        >
          {!disableClose && (
            <div onClick={() => setShow()} className={styles.propdocsModal_content__closeButton}>
              <CloseIcon className={styles.propdocsModal_content__closeIcon} />
            </div>
          )}
          <div className={styles.propdocsModal_content}>
            <div className={classNames(styles.propdocsModal_content__header, 'modal-header')}>
              <h2 className={styles.propdocsModal_content__title}>{title}</h2>
            </div>
            <div className={styles.propdocsModal_content__body}>{children}</div>
          </div>
        </div>
      </div>
    </div>
  );

  // Use React Portal to render directly to document.body
  return createPortal(modalElement, document.body);
}

export default PropdocsModal;
